import { Trans } from '@lingui/macro'
import { CheckOutlined, ClearOutlined } from '@mui/icons-material'
import { FilledInputProps, Grid, InputAdornment, InputProps, MenuItem, OutlinedInputProps, TextField, TextFieldProps, Typography } from '@mui/material'
import { ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { bilingualNumberFormat, getCurrentWords, PhoneCountryCodeFormat, PhoneFormat, PhoneFormatWithExtension } from 'app/views/common/Formats'
import { EMAIL_FORMAT_HELPER_TEXT, PHONE_NUMBER_FORMAT_HELPER_TEXT, URL_FORMAT_HELPER_TEXT } from 'app/views/forms/formTranslations'
import { Field, useField } from 'formik'
import { isNumber } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import NumberFormat from 'react-number-format'
import { useSelector } from 'react-redux'
import { valueExistsTS } from 'utilsTS'
import { countHelperText } from '../FormElement'
import { ErrorLabel } from '../forms/ErrorLabel'
import { FormTopLabel } from '../forms/FormTopLabel'

interface TextFieldWithFormikProps {
  maxWords?: number
  maxInput?: number
  minInput?: number
  limit?: number
  required?: boolean
  currency?: boolean
  type?: 'text' | 'number' | 'currency' | 'countryCode' | 'phone' | 'phoneWithExtension' | 'url' | 'email'
  language?: 'en' | 'fr' | 'iu-cans'
  /**
   * This is an id of Formiks field that the TextField is connected to
   */
  name: string
  isTopLabel?: boolean // If true, label will be on top of the input, otherwise it will be inside the input
  innerRef?: React.RefObject<HTMLInputElement>
  showErrorWithoutTouch?: boolean
  helperArray?: Array<string | JSX.Element>
  actionIconButton?: React.ReactNode
  wordCount?: boolean
  multiline?: boolean
  topLabel?: React.ReactNode
}

/**
 * Convinience wrapper for Material UI Text Field connected to Formik
 * @category Common components
 * @subcategory Formik
 * @component
 * @returns {JSX.Element}
 * @property {string} name This is an id of Formiks field that the TextField is connected to
 */
export const TextFieldWithFormik = (
  {
    name,
    type = 'text',
    limit,
    topLabel,
    required = false,
    maxInput,
    minInput,
    maxWords,
    disabled = false,
    isTopLabel,
    innerRef,
    select = false,
    wordCount = false,
    variant = 'outlined',
    showErrorWithoutTouch = false,
    actionIconButton,
    children,
    multiline = false,
    ...props
  }: TextFieldProps & TextFieldWithFormikProps & TextFieldWithFormikProps
): JSX.Element => {
  const user = useSelector<ReduxState, ReduxUser>(state => state.user)
  const [field, meta, helpers] = useField(name)
  const fieldValue = field.value ?? ''
  const [inputValue, setInputValue] = useState(fieldValue)
  const [defaultZeroRemoved, setDefaultZeroRemoved] = useState(false)
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
  const [startEditingValue, setStartEditingValue] = useState(null)
  const helperArray = valueExistsTS(props.helperArray) ? [...props.helperArray] : []

  const language = props.language ?? user.language

  const { touched = false } = meta

  const inputChanged = (e, validateOnKeystroke): void => {
    let value = e.target.value
    if (value === inputValue) {
      return
    }
    let invalid = false
    if (isNumber(maxWords)) {
      const words = +getCurrentWords(e.target.value)
      if (words > maxWords) {
        invalid = true
      } else if (words === maxWords) {
        value = value.trim()
      }
    }
    if (!invalid) {
      setInputValue(value)
    } else {
      setInputValue(inputValue)
    }
    if (timer != null) {
      clearTimeout(timer)
    }
    const newTimer = setTimeout(() => {
      helpers.setValue(value, validateOnKeystroke)
    }, 700)
    setTimer(newTimer)
  }

  useEffect(() => {
    setInputValue(fieldValue)
  }, [fieldValue])

  const NumberFormatWithMinAndMax = useCallback(
    (props) => {
      const { inputRef, id, value = '', onChange, ...other } = props
      let formattedValue: string | number = ''
      // noinspection JSIncompatibleTypesComparison
      if (isNumber(value)) {
        const num = String(value).replaceAll(',', '')
        formattedValue = parseFloat(Number(num).toFixed(2))
      }
      let separator = ','
      if (language !== 'en' && type === 'currency') {
        separator = ' '
      }
      return (
        <NumberFormat
          {...other}
          isAllowed={({ floatValue }) => {
            if (!isNumber(floatValue)) {
              return true
            }
            if (valueExistsTS(maxInput)) {
              return floatValue <= Number(maxInput)
            }
            return true
          }}
          value={typeof props.value === 'object' ? '' : value}
          defaultValue=''
          thousandSeparator={separator}
          isNumericString={false}
          getInputRef={inputRef}
          allowNegative={false}
          suffix={language !== 'en' && type === 'currency' ? ' $' : ''}
          prefix={language === 'en' && type === 'currency' ? '$' : ''}
          allowLeadingZeros={false}
          onValueChange={(values) => {
            onChange({
              target: {
                name: props.name,
                value: values.value
              }
            })
          }}
        />
      )
    },
    [maxInput, minInput, type, language]
  )

  const baseProps: Partial<FilledInputProps> | Partial<OutlinedInputProps> | Partial<InputProps> = {}

  if (type === 'number' || type === 'currency') {
    baseProps.inputComponent = NumberFormatWithMinAndMax
  } else if (type.includes('phone')) {
    baseProps.inputComponent = type === 'phoneWithExtension'
      ? PhoneFormatWithExtension
      : PhoneFormat
    helperArray.push(<span>
      {PHONE_NUMBER_FORMAT_HELPER_TEXT}
      .
    </span>)
  } else if (type === 'countryCode') {
    baseProps.inputComponent = PhoneCountryCodeFormat
    limit = 5
  } else if (type === 'url') {
    helperArray.push(<span>
      {URL_FORMAT_HELPER_TEXT}
      .
    </span>)
  } else if (type === 'email') {
    helperArray.push(<span>
      {EMAIL_FORMAT_HELPER_TEXT}
      .
    </span>)
  }

  const stringValue = inputValue ?? ''
  if (valueExistsTS(wordCount) || valueExistsTS(maxWords)) {
    helperArray.push(
      <span key='wordsCount'>
        {getCurrentWords(stringValue, maxWords)} <Trans>words</Trans>
        {'. '}
      </span>
    )
  }

  if (type === 'number' || type === 'currency') {
    helperArray.push(<span key='numPerm'><Trans>
      Only numerical values are permitted in this field
    </Trans>
    </span>)

    if (valueExistsTS(minInput)) {
      helperArray.push(
        <span key={1}>. </span>,
        <Trans>
          Minimum value is: {bilingualNumberFormat(minInput, language)}
        </Trans>
      )
    }
    if (valueExistsTS(maxInput)) {
      helperArray.push(
        <span key={1}>'. '</span>,
        <Trans>
          Maximum value is: {bilingualNumberFormat(maxInput, language)}
        </Trans>
      )
    }
  }

  // else {
  //   if (required) {
  //     helperArray.push(
  //       <span key='required'>
  //         <Trans>Required</Trans>
  //         {'. '}
  //       </span>
  //     )
  //   }
  // }

  let marginBottom: number | string = 0
  const style: React.CSSProperties = props.style ?? { marginTop: 5 }
  if (type === 'countryCode') {
    style.width = 95
  }
  if (valueExistsTS(style.marginBottom)) {
    marginBottom = style.marginBottom ?? style.margin ?? 0
    style.marginBottom = marginBottom
  }

  return (
    <Field name={name}>
      {({ field, meta, form }) => {
        const displayError = (touched || showErrorWithoutTouch) && meta.error
        const isRequiredError = meta?.error?.props?.message?.toLowerCase()?.includes('required')
        let inputError = false
        if (maxInput && inputValue) {
          if (+maxInput < +inputValue) {
            inputError = true
          }
        }
        if (minInput && inputValue) {
          if (+minInput > +inputValue) {
            inputError = true
          }
        }
        if (isNumber(limit)) {
          if (+limit < stringValue.length) {
            inputError = true
          }
        }

        let valueToShow = inputValue
        if (!valueToShow && valueToShow !== 0 && valueToShow !== '0') {
          valueToShow = props.defaultValue ?? ''
        }
        const showError = (meta.error && (touched || showErrorWithoutTouch)) || inputError

        const toReturn = (
          <>
            {topLabel &&
              <FormTopLabel small required={required}>
              {topLabel}
            </FormTopLabel>}
            <TextField
              name={name}
              value={valueToShow}
              onChange={e => {
                if (props.onChange != null) {
                  props.onChange(e)
                } else {
                  inputChanged(e, touched)
                }
              }}
              sx={{
                '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input': {
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center'
                },
              }}
              error={showError}
              InputProps={{
                ...baseProps,
                endAdornment: required && !disabled && (touched || valueToShow)
                  ? (
                    <InputAdornment position='end'>
                      {!showError
                        ? (
                          <CheckOutlined
                            sx={{
                              fontWeight: 700,
                              fontSize: '24px',
                              color: 'success.main',
                              marginRight: select ? '15px' : 'initial'
                            }}
                          />
                          )
                        : (
                          <ClearOutlined
                            sx={{
                              color: 'error.main',
                              fontWeight: 700,
                              fontSize: '24px',
                              marginRight: select ? '15px' : 'initial'
                            }}
                          />
                          )}
                    </InputAdornment>
                    )
                  : (
                      ''
                    )
              }}
              inputProps={{
                maxLength: isNumber(limit) && limit,
                multiline
              }}
              select={select}
              variant={variant}
              fullWidth
              helperText={
                <Grid container>
                  <Grid item xs>
                    {displayError ? <ErrorLabel label={meta.error} /> : helperArray}
                  </Grid>
                  {isNumber(limit) && type !== 'countryCode' &&
                    <Grid item sx={{ color: 'black !important', textAlign: 'right' }}>
                      {countHelperText(stringValue, Number(limit))}
                    </Grid>}
                </Grid>
              }
              type='text'
              onFocus={e => {
                if (startEditingValue === null) {
                  setStartEditingValue(inputValue)
                }
                if (type === 'number' && (valueToShow === 0 || valueToShow === '0')) {
                  setDefaultZeroRemoved(true)
                  setInputValue('')
                }
              }}
              onBlur={e => {
                if (timer != null) {
                  clearTimeout(timer)
                }
                if (!touched) {
                  form.setFieldTouched(name)
                }
                helpers.setValue(inputValue)
                setStartEditingValue(null)
                if (type === 'number' && !inputValue && defaultZeroRemoved) {
                  setDefaultZeroRemoved(false)
                  setInputValue('0')
                } else if (defaultZeroRemoved) {
                  setDefaultZeroRemoved(false)
                }
                e.stopPropagation()
                e.preventDefault()
              }}
              disabled={disabled}
              {...props}
              multiline={multiline}
              style={{ wordBreak: 'break-word', ...style }}
            >
              {select &&
                <MenuItem disabled value=''>
                  <Typography sx={{ fontWeight: 400 }}>
                    <Trans>SELECT_ONE</Trans>
                  </Typography>
                </MenuItem>}
              {children}
            </TextField>
          </>
        )

        if (actionIconButton) {
          return (
            <Grid
              container
              direction='row'
              justifyContent='center'
              alignItems='flex-start'
              gap='8px'
            >
              <Grid item xs>
                {toReturn}
              </Grid>
              <Grid item sx={{ paddingTop: '8px' }}>
                {actionIconButton}
              </Grid>
            </Grid>
          )
        } else {
          return toReturn
        }
      }}
    </Field>
  )
}
